import { useDeepCompareMemo } from 'use-deep-compare';
import { LayerEnum } from '../mapSlice';
import { LayerExistenceDict } from './useCheckLayers';

// Ordered bottom layer to top layer
const layerOrder: LayerEnum[] = [
  LayerEnum.blockOutlines,
  LayerEnum.blockNames,
  LayerEnum.heatmap,
  LayerEnum.heatmapCurrentPoint,
];

/**
 * Custom hook to determine the layer order within 'react-map-gl'
 * @param currentLayer {LayerEnum} - The current layer
 * @param layerExistenceDict {LayerExistenceDict} - A dictionary of layers and whether they exist
 * @returns {LayerEnum | undefined} - The layer after the current one if it exists, otherwise undefined
 */
const useLayerOrder = (
  currentLayer: LayerEnum,
  layerExistenceDict: LayerExistenceDict
): LayerEnum | undefined => {
  // Memoize the result to improve performance
  const beforeId = useDeepCompareMemo(() => {
    // Find the index of the current layer in the layerOrder array
    const currentIndex = layerOrder.indexOf(currentLayer);

    // If it's the last layer or not found in the array, return undefined
    if (currentIndex < 0 || currentIndex === layerOrder.length - 1)
      return undefined;

    // Loop through the layers after the current one to find the next existing layer
    for (let i = currentIndex + 1; i < layerOrder.length; i++) {
      const layerAfterCurrent = layerOrder[i];
      const layerAfterExists = layerExistenceDict[layerAfterCurrent];
      if (layerAfterExists) {
        return layerAfterCurrent;
      }
    }

    return undefined; // If no layer after the current one exists
  }, [layerExistenceDict, currentLayer]);

  return beforeId;
};

export default useLayerOrder;
